import {
  BasicTask,
  BasicTaskSchema,
  FavoriteSearch,
  FavoriteSearchSchema,
  SearchParamsObj,
  SearchParamsObjSchema,
  SearchTaskBasicSchema,
  ShortAppUser,
  ShortAppUserSchema,
  TagCategory,
  TagCategorySchema,
  UserTimer,
  UserTimerSchema,
} from "@/types";
import {
  validateArrayWithSchema,
  validateWithSchema,
} from "@/utils/dataValidators.zod";
import localforage from "localforage";
import { z } from "zod";

// Define schema for basic search tasks with timestamp
export const SearchTaskBasicWithTimestampSchema = z.object({
  tasks: z.array(SearchTaskBasicSchema),
  timestamp: z.number(),
});

export type SearchTaskBasicWithTimestamp = z.infer<
  typeof SearchTaskBasicWithTimestampSchema
>;

export async function clearAllLocFor(): Promise<void> {
  await localforage.clear();
}

// #region User tasks
export async function locForSetUserTasks(tasks: BasicTask[]): Promise<void> {
  await localforage.setItem("all-user-tasks", tasks);
}

export async function locForGetUserTasks(): Promise<BasicTask[] | null> {
  const localforageData = await localforage.getItem("all-user-tasks");
  if (!localforageData) {
    return null;
  }

  const validatedData = validateArrayWithSchema<BasicTask>(
    localforageData,
    BasicTaskSchema,
    "Basic Task"
  );

  if (validatedData) {
    return validatedData;
  }

  console.error("locForGetUserTasks: Invalid tasks", localforageData);
  await clearAllLocFor();
  window.location.reload();
  return [];
}
// #endregion User tasks

// #region App users
export async function locForSetAllUsers(users: ShortAppUser[]): Promise<void> {
  await localforage.setItem("all-app-users", users);
}

export async function locForGetAllUsers(): Promise<ShortAppUser[] | null> {
  const localforageData = await localforage.getItem("all-app-users");
  if (!localforageData) {
    return null;
  }

  const validatedData = validateArrayWithSchema<ShortAppUser>(
    localforageData,
    ShortAppUserSchema,
    "ShortAppUser"
  );

  if (validatedData) {
    return validatedData;
  }

  console.error("locForGetAllUsers: Invalid users", localforageData);
  await clearAllLocFor();
  window.location.reload();
  return null;
}
// #endregion App users

// #region User tags
export async function locForSetUserTags(tags: TagCategory[]): Promise<void> {
  await localforage.setItem("all-user-tags", tags);
}

export async function locForGetUserTags(): Promise<TagCategory[] | null> {
  const localforageData = await localforage.getItem("all-user-tags");
  if (!localforageData) {
    return null;
  }

  const validatedData = validateArrayWithSchema<TagCategory>(
    localforageData,
    TagCategorySchema,
    "TagCategory"
  );

  if (validatedData) {
    return validatedData;
  }

  console.error("locForGetUserTags: Invalid tags", localforageData);
  await clearAllLocFor();
  window.location.reload();
  return null;
}
// #endregion User tags

// #region User timers
export async function locForSetUserTimers(timers: UserTimer[]): Promise<void> {
  await localforage.setItem("all-user-timers", timers);
}

export async function locForGetUserTimers(): Promise<UserTimer[] | null> {
  const localforageData = await localforage.getItem("all-user-timers");
  if (!localforageData) {
    return null;
  }

  const validatedData = validateArrayWithSchema<UserTimer>(
    localforageData,
    UserTimerSchema,
    "UserTimer"
  );

  if (validatedData) {
    return validatedData;
  }

  console.error("locForGetUserTimers: Invalid timers", localforageData);
  await clearAllLocFor();
  window.location.reload();
  return null;
}

// #endregion User timers

// #region PreCache Tasks
export async function locForSetPreCacheTasks(
  tasks: SearchTaskBasicWithTimestamp
): Promise<void> {
  console.log("Setting preCache tasks", tasks);
  await localforage.setItem("preCache-tasks", tasks);
}

export async function locForGetPreCacheTasks(): Promise<SearchTaskBasicWithTimestamp | null> {
  const localforageData = await localforage.getItem("preCache-tasks");
  if (!localforageData) {
    return null;
  }

  const validatedData = validateWithSchema<SearchTaskBasicWithTimestamp>(
    localforageData,
    SearchTaskBasicWithTimestampSchema,
    "SearchTaskBasic"
  );
  // console.log("Validated preCache tasks", validatedData);

  if (!validatedData) {
    console.error("locForGetPreCacheTasks: Invalid tasks", localforageData);
    await localforage.removeItem("preCache-tasks");
    return null;
  }

  return validatedData;
}

// #endregion PreCache Tasks

// #region Recent Searches

// Get all recent searches
export async function locForGetRecentSearches(): Promise<string[]> {
  const queries = await localforage.getItem("recent-searches");
  console.log("queries", queries);
  if (!queries || !Array.isArray(queries)) {
    return [];
  }

  return queries;
}

// Save the first 5 searches, if there are more than 5, remove the last one
export async function locForSetRecentSearches(query: string): Promise<void> {
  const queries = await locForGetRecentSearches();
  // if query is in queries, return
  if (queries.includes(query)) {
    return;
  }

  console.log("queries", queries);

  if (queries.length >= 5) {
    queries.pop();
  }

  queries.unshift(query);
  await localforage.setItem("recent-searches", queries);
}

// #endregion Recent Searches

// #region Favorite Searches
export async function locForGetFavoriteSearches(): Promise<
  FavoriteSearch[] | null
> {
  const searches = await localforage.getItem("favorite-searches");
  if (!searches || !Array.isArray(searches)) {
    return null;
  }

  const validatedData = validateArrayWithSchema<FavoriteSearch>(
    searches,
    FavoriteSearchSchema,
    "FavoriteSearch"
  );

  if (!validatedData) {
    return null;
  }

  return validatedData;
}

export async function locForSetFavoriteSearches(
  favoriteSearches: FavoriteSearch[]
): Promise<void> {
  await localforage.setItem("favorite-searches", favoriteSearches);
}

export async function locForClearFavoriteSearches(): Promise<void> {
  await localforage.removeItem("favorite-searches");
}

// #region Persist Search Params

// get search params object
export async function LSgetSearchParams(): Promise<SearchParamsObj> {
  const searchParams = await localforage.getItem("persist-params");
  if (!searchParams) {
    return {
      assignedTo: [],
      collaboratorInput: [],
      collaboratorSearchType: "exact",
      status: null,
      title: null,
      dueDateType: null,
      dueDateData: null,
      tagData: [],
      tagType: "exact",
      createdDateType: null,
      createdDateData: null,
      completedDateType: null,
      completedDateData: null,
      modifiedDateType: null,
      modifiedDateData: null,
      assignedBy: [],
      createdBy: [],
      updatedBy: [],
      hasTimer: [],
      inverseFilters: [],
    };
  }

  const validatedData = validateWithSchema(
    searchParams,
    SearchParamsObjSchema,
    "SearchParams"
  );

  if (!validatedData) {
    return {
      assignedTo: [],
      collaboratorInput: [],
      collaboratorSearchType: "exact",
      status: null,
      title: null,
      dueDateType: null,
      dueDateData: null,
      tagData: [],
      tagType: "exact",
      createdDateType: null,
      createdDateData: null,
      completedDateType: null,
      completedDateData: null,
      modifiedDateType: null,
      modifiedDateData: null,
      assignedBy: [],
      createdBy: [],
      updatedBy: [],
      hasTimer: [],
      inverseFilters: [],
    };
  }

  return validatedData;
}

// set search params object
export async function LSsetSearchParams(
  params: SearchParamsObj
): Promise<void> {
  await localforage.setItem("persist-params", params);
}

export async function LSClearSearchParams(): Promise<void> {
  const defaultParams: SearchParamsObj = {
    assignedTo: [],
    collaboratorInput: [],
    collaboratorSearchType: "exact",
    title: "",
    status: null,
    dueDateType: null,
    dueDateData: null,
    tagData: [],
    tagType: "exact",
    createdDateType: null,
    createdDateData: null,
    completedDateType: null,
    completedDateData: null,
    modifiedDateType: null,
    modifiedDateData: null,
    assignedBy: [],
    createdBy: [],
    updatedBy: [],
    hasTimer: [],
    inverseFilters: [],
  };
  await LSsetSearchParams(defaultParams);
}

// #endregion Persist Search Params

// #region TimerLocks

export const createLock = async (): Promise<string> => {
  await localforage.setItem("timerLock", "locked");

  const channel = new BroadcastChannel("localforage_channel");
  channel.postMessage("timerLockCreated");
  channel.close();
  return "locked";
};

export const removeLock = async (): Promise<null> => {
  await localforage.removeItem("timerLock");

  const channel = new BroadcastChannel("localforage_channel");
  channel.postMessage("timerLockRemoved");
  channel.close();
  return null;
};

export const checkLock = async (): Promise<string | null> => {
  const lockState: string | null = await localforage.getItem("timerLock");
  return lockState;
};

// #endregion TimerLocks
